import { IApiResponse } from "../../../@types/api-response.type";
import {
  AllIdleResourceDto,
  AllNonScheduledResourceDto,
  ICapacitySummary,
  ICompanyCapacity,
  ICostOfIdleness,
  IOverCostProjects,
  IPositionCapacity,
  IProjectVision,
  IAllUnavailableResourcesDto,
  ProjectsWithoutAllocationsDto,
  ProjectsWithoutBudgetsDto,
  ResourcesCostOfIdlenessDto,
  IUnavailableResourcesDto,
} from "../../../@types/widgets.type";
import { Api } from "../ApiConfig";
import { ApiException } from "../ApiException";
import { getErrorMessage } from "../../../utils/utils";

export const getAllWidgets = async () => {
  try {
    const { data } = await Api.get(`/api/Widget/GetAll`);
    return data.result;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar todos os widgets'));
  }
};

export const getCompanyCapacity = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<ICompanyCapacity> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<ICompanyCapacity>>(
      `/api/HomePage/GetCompanyCapacity`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getUnavailableResources = async (params: {
  managerId: string;
  skip: number;
  take: number;
}): Promise<IApiResponse<IUnavailableResourcesDto> | ApiException> => {
  try {
    const { data } = await Api.post<IApiResponse<IUnavailableResourcesDto>>(
      `/api/HomePage/GetUnavailableResources`,
      params
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getAllUnavailableResources = async (
  skip: number,
  take: number,
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<IAllUnavailableResourcesDto> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<IAllUnavailableResourcesDto>>(
      `/api/HomePage/GetAllUnavailableResources?skip=${skip}&take=${take}`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getAllProjectWithoutBudget = async (
  skip: number,
  take: number,
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<ProjectsWithoutBudgetsDto> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<ProjectsWithoutBudgetsDto>>(
      `/api/HomePage/GetAllProjectWithoutBudget?skip=${skip}&take=${take}`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getAllProjectWithoutAllocation = async (params: {
  skip: number;
  take: number;
  projectStatusIds: string[];
  userEmail: string;
  resourceId: string;
}): Promise<IApiResponse<ProjectsWithoutAllocationsDto> | ApiException> => {
  const { userEmail, resourceId, ...restParams } = params;

  try {
    const { data } = await Api.post<
      IApiResponse<ProjectsWithoutAllocationsDto>
    >(`/api/HomePage/GetProjectWithoutAllocation`, restParams, {
      headers: {
        userEmail,
        "Resource-Id": resourceId,
      },
    });
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getAllNonScheduledResource = async (params: {
  managerId: string;
  startTime: string;
  endTime: string;
  skip: number;
  take: number;
}): Promise<IApiResponse<AllNonScheduledResourceDto> | ApiException> => {
  try {
    const { data } = await Api.post<IApiResponse<AllNonScheduledResourceDto>>(
      `/api/HomePage/GetAllNonScheduledResource`,
      params
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getResourcesCostOfIdleness = async (params: {
  managerId: string;
  skip: number;
  take: number;
}): Promise<IApiResponse<ResourcesCostOfIdlenessDto> | ApiException> => {
  try {
    const { data } = await Api.post<IApiResponse<ResourcesCostOfIdlenessDto>>(
      `/api/HomePage/GetResourcesCostOfIdleness`,
      params
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getAllIdleResources = async (params: {
  startTime: string;
  endTime: string;
  skip: number;
  take: number;
}): Promise<IApiResponse<AllIdleResourceDto> | ApiException> => {
  try {
    const { data } = await Api.post<IApiResponse<AllIdleResourceDto>>(
      `/api/HomePage/GetAllIdleResources`,
      params
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getCostOfIdleness = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<ICostOfIdleness[]> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<ICostOfIdleness[]>>(
      `/api/HomePage/GetCostOfIdleness/${resourceId}`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getOverCostProjects = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<IOverCostProjects[]> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<IOverCostProjects[]>>(
      `/api/HomePage/GetOverCostProjects`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getCompanyCapacitySummary = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<ICapacitySummary> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<ICapacitySummary>>(
      `/api/HomePage/GetCompanyCapacitySummary`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getMyResourcesCapacitySummary = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<ICapacitySummary> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<ICapacitySummary>>(
      `/api/HomePage/GetMyResourcesCapacitySummary/${resourceId}`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getPositionCapacity = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<IPositionCapacity> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<IPositionCapacity>>(
      `/api/HomePage/GetPositionCapacity`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};

export const getProjectVision = async (
  userEmail: string,
  resourceId: string
): Promise<IApiResponse<IProjectVision[]> | ApiException> => {
  try {
    const { data } = await Api.get<IApiResponse<IProjectVision[]>>(
      `/api/HomePage/GetProjectVision`,
      {
        headers: {
          userEmail,
          "Resource-Id": resourceId,
        },
      }
    );
    return data;
  } catch (error) {
    throw new ApiException(getErrorMessage(error, 'Erro ao buscar os dados'));
  }
};
